import type { DiaryEntry } from "../calendar/entries.js";

/**
 * Pure helpers for the Today screen: what is happening now, what is next, and
 * how the day at each school looks. Kept free of React so they can be tested
 * against fixed clocks.
 */

export type ReadableEntry = DiaryEntry & { anonymous: false };

export type UpNextState =
  | { kind: "none" }
  | { kind: "up-next"; next: ReadableEntry; startsInMs: number; remaining: number }
  | {
      kind: "in-progress";
      current: ReadableEntry;
      endsInMs: number;
      next: ReadableEntry | null;
      remaining: number;
    }
  | { kind: "done"; taught: number; lastEndedAt: string };

function byStart(entries: readonly ReadableEntry[]): ReadableEntry[] {
  return [...entries].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}

/**
 * Where the day stands at `now`.
 *
 * A lesson counts as in progress from its start until its end; the next one
 * is the first that has not started. Once every lesson has ended the day is
 * done, however many there were.
 */
export function upNextState(entries: readonly ReadableEntry[], now: number): UpNextState {
  const ordered = byStart(entries);
  if (ordered.length === 0) return { kind: "none" };

  const current = ordered.find(
    (entry) => Date.parse(entry.startsAt) <= now && Date.parse(entry.endsAt) > now,
  );
  const upcoming = ordered.filter((entry) => Date.parse(entry.startsAt) > now);
  const next = upcoming[0] ?? null;

  if (current) {
    return {
      kind: "in-progress",
      current,
      endsInMs: Date.parse(current.endsAt) - now,
      next,
      remaining: upcoming.length,
    };
  }
  if (next) {
    return {
      kind: "up-next",
      next,
      startsInMs: Date.parse(next.startsAt) - now,
      remaining: upcoming.length,
    };
  }
  const last = ordered.at(-1)!;
  return { kind: "done", taught: ordered.length, lastEndedAt: last.endsAt };
}

/** "in 12 min", "in 1 h 5 min", "in under a minute". */
export function describeCountdown(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "in under a minute";
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `in ${hours} h` : `in ${hours} h ${rest} min`;
}

export interface SchoolDaySummary {
  school: string;
  pupils: number;
  firstStartsAt: string;
  lastEndsAt: string;
  notesOutstanding: number;
  attendanceMissing: number;
}

/** The school a diary row belongs to, from the "School · Name" category the Hub projects. */
export function schoolOf(entry: ReadableEntry): string | null {
  if (!entry.isSchools) return null;
  const category = entry.categoryName ?? "";
  const match = /^School\s*·\s*(.+)$/u.exec(category);
  if (match?.[1]) return match[1].trim();
  return category.trim() || "School";
}

/**
 * One line per school taught today: how many pupils, when it starts and
 * ends, and what is still owed once it is over.
 */
export function schoolDaySummary(
  entries: readonly ReadableEntry[],
  now: number,
): SchoolDaySummary[] {
  const bySchool = new Map<string, ReadableEntry[]>();
  for (const entry of entries) {
    const school = schoolOf(entry);
    if (school === null) continue;
    const list = bySchool.get(school) ?? [];
    list.push(entry);
    bySchool.set(school, list);
  }
  return [...bySchool.entries()]
    .map(([school, lessons]) => {
      const ordered = byStart(lessons);
      const started = ordered.filter((entry) => Date.parse(entry.startsAt) < now);
      return {
        school,
        pupils: new Set(ordered.map((entry) => entry.bookingId)).size,
        firstStartsAt: ordered[0]!.startsAt,
        lastEndsAt: ordered.reduce(
          (latest, entry) =>
            Date.parse(entry.endsAt) > Date.parse(latest) ? entry.endsAt : latest,
          ordered[0]!.endsAt,
        ),
        notesOutstanding: started.filter((entry) => entry.lessonPublishedAt === null).length,
        attendanceMissing: started.filter((entry) => entry.attendanceStatus === null).length,
      };
    })
    .sort((a, b) => Date.parse(a.firstStartsAt) - Date.parse(b.firstStartsAt));
}

/** The session and pupil ids inside a canonical School booking id, or null for anything else. */
export function schoolIdentityOf(
  entry: ReadableEntry,
): { sessionId: string; pupilId: string } | null {
  if (!entry.isSchools) return null;
  const parts = entry.bookingId.split(":");
  if (parts.length !== 3 || parts[0] !== "school") return null;
  return { sessionId: parts[1]!, pupilId: parts[2]! };
}
